import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { PaginacaoComponent } from '../../../../shared/components/paginacao/paginacao.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { NoticiaCardComponent } from '../noticia-card/noticia-card.component';
import { NoticiasService } from '../../services/noticias.service';
import { NoticiaDestaqueComponent } from './noticia-destaque/noticia-destaque.component';
import { LinksDinamicosComponent } from '../../links-dinamicos/links-dinamicos.component';
import { ScrollNavegacaoService } from '../../../../core/services/scroll-navegacao.service';
import { NOTICIA_FILTRO_TODOS, NoticiaCategoria } from '../../models/noticia.model';

const NOTICIAS_POR_PAGINA = 9;

/** A busca vai ao servidor: espera a pessoa parar de digitar, para não gastar o limite de leitura. */
const ESPERA_BUSCA_MS = 400;

@Component({
  selector: 'app-todas-noticias',
  templateUrl: './todas-noticias.component.html',
  styleUrls: ['./todas-noticias.component.css'],
  imports: [
    LinksDinamicosComponent,
    BreadcrumbComponent,
    LucideModule,
    NoticiaCardComponent,
    NoticiaDestaqueComponent,
    PaginacaoComponent,
  ],
})
export class TodasNoticiasComponent {
  private readonly noticiasService = inject(NoticiasService);
  private readonly injector = inject(Injector);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);

  private readonly listaNoticias = viewChild<ElementRef<HTMLElement>>('listaNoticias');

  private esperaBusca: ReturnType<typeof setTimeout> | undefined;

  private readonly categoriasNoticia = this.noticiasService.categorias();

  readonly filtros = computed<readonly NoticiaCategoria[]>(() => [
    NOTICIA_FILTRO_TODOS,
    ...this.categoriasNoticia.categorias(),
  ]);

  readonly categoriaAtiva = signal(NOTICIA_FILTRO_TODOS.id);

  /** O texto do campo, atualizado a cada tecla. */
  readonly termoBusca = signal('');

  /** O texto que já foi ao servidor. */
  private readonly buscaAplicada = signal('');

  readonly paginaAtual = linkedSignal({
    source: () => ({ termo: this.buscaAplicada(), categoria: this.categoriaAtiva() }),
    computation: () => 1,
  });

  private readonly pagina = this.noticiasService.listagem(() => ({
    categoria: this.categoriaAtiva() === NOTICIA_FILTRO_TODOS.id ? null : Number(this.categoriaAtiva()),
    busca: this.buscaAplicada(),
    pagina: this.paginaAtual(),
    tamanho: NOTICIAS_POR_PAGINA,
  }));

  readonly carregando = this.pagina.carregando;

  readonly noticiasDaPagina = this.pagina.noticias;

  readonly totalResultados = this.pagina.total;

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.totalResultados() / NOTICIAS_POR_PAGINA))
  );

  readonly temFiltroAtivo = computed(
    () => !!this.termoBusca() || this.categoriaAtiva() !== NOTICIA_FILTRO_TODOS.id
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.esperaBusca));
  }

  selecionarCategoria(categoriaId: string): void {
    this.categoriaAtiva.set(categoriaId);
  }

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);

    clearTimeout(this.esperaBusca);
    this.esperaBusca = setTimeout(() => this.buscaAplicada.set(this.termoBusca().trim()), ESPERA_BUSCA_MS);
  }

  limparBusca(): void {
    clearTimeout(this.esperaBusca);
    this.termoBusca.set('');
    this.buscaAplicada.set('');
  }

  limparTudo(): void {
    this.limparBusca();
    this.categoriaAtiva.set(NOTICIA_FILTRO_TODOS.id);
  }

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);

    afterNextRender(() => this.rolarParaLista(), { injector: this.injector });
  }

  private rolarParaLista(): void {
    this.scrollNavegacao.aoElemento(this.listaNoticias()?.nativeElement);
  }
}
